<template>
  <nav class="app-bar__nav">
    <div class="app-bar__links">
      <router-link to="/catalog" class="app-bar__nav-link app-bar__nav-link--catalog" data-tutorial="catalog-link">
        <svg
          class="app-bar__nav-link-icon"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
          aria-hidden="true"
        >
          <rect x="3" y="3" width="7" height="7" rx="1.5" />
          <rect x="14" y="3" width="7" height="7" rx="1.5" />
          <rect x="3" y="14" width="7" height="7" rx="1.5" />
          <rect x="14" y="14" width="7" height="7" rx="1.5" />
        </svg>
        Каталог
      </router-link>
      <router-link v-if="isSupplierShowcaseEnabled" to="/suppliers" class="app-bar__nav-link">
        Поставщики
      </router-link>
      <UiButton
        v-if="(isBuyer || isSupplier || isAdmin) && isProcurementEnabled"
        variant="secondary"
        :to="'/procurements'"
      >
        Закупки
      </UiButton>
      <UiButton v-else-if="isAdmin && !isProcurementEnabled" variant="secondary" :disabled="true">
        Закупки
        <UiBadge variant="neutral">Откл.</UiBadge>
      </UiButton>
    </div>
    <span class="app-bar__divider" aria-hidden="true" />
    <div class="app-bar__actions">
      <UiButton
        v-if="!isSupplier"
        :to="'/cart'"
        variant="secondary"
        class="app-bar__nav-btn app-bar__nav-btn--icon"
        aria-label="Корзина"
        data-tutorial="cart-btn"
      >
        <span class="app-bar__badge-wrap">
          🛒
          <UiBadge v-if="cartItemsCount > 0" variant="accent" class="app-bar__badge">
            {{ cartItemsCount }}
          </UiBadge>
        </span>
      </UiButton>
      <UiButton
        :to="'/favorites'"
        variant="secondary"
        class="app-bar__nav-btn app-bar__nav-btn--icon"
        aria-label="Избранное"
      >
        <span class="app-bar__badge-wrap">
          ♡
          <UiBadge v-if="favoritesCount > 0" variant="accent" class="app-bar__badge">
            {{ favoritesCount }}
          </UiBadge>
        </span>
      </UiButton>
      <NotificationCenter v-if="isAuthenticated" class="app-bar__notifications" />
      <UiMenu v-if="isAuthenticated" ref="userMenuRef" align="right" class="app-bar__user-menu">
        <template #trigger>
          <button type="button" class="app-bar__user-trigger" data-tutorial="profile-menu">
            👤 <span class="app-bar__user-name">{{ userName }}</span>
          </button>
        </template>
        <router-link to="/profile" class="app-menu__item" @click="closeUserMenu">Профиль</router-link>
        <router-link
          v-if="isSupplier"
          to="/supplier/dashboard"
          class="app-menu__item"
          @click="closeUserMenu"
        >
          Кабинет поставщика
        </router-link>
        <template v-if="!isSupplier">
          <router-link
            :to="isAdmin ? '/admin/orders' : '/orders'"
            class="app-menu__item"
            @click="closeUserMenu"
          >
            Заказы
          </router-link>
        </template>
        <router-link v-if="isChatEnabled" to="/chats" class="app-menu__item" @click="closeUserMenu">
          Сообщения
        </router-link>
        <template v-if="isAdmin">
          <div class="app-menu__divider" />
          <div class="app-menu__label">Администрирование</div>
        </template>
        <router-link v-if="isAdmin" to="/admin/dashboard" class="app-menu__item" @click="closeUserMenu">
          Админка
        </router-link>
        <router-link v-if="isAdmin" to="/admin/analytics" class="app-menu__item" @click="closeUserMenu">
          Аналитика
        </router-link>
        <router-link v-if="isAdmin" to="/admin/tools" class="app-menu__item" @click="closeUserMenu">
          Инструменты
        </router-link>
        <router-link
          v-if="((isBuyer || isSupplier) && isProcurementEnabled) || isAdmin"
          :to="isProcurementEnabled ? '/procurements' : '#'"
          class="app-menu__item"
          @click="closeUserMenu"
        >
          Закупки
          <UiBadge v-if="isAdmin && !isProcurementEnabled" variant="neutral">Откл.</UiBadge>
        </router-link>
        <button type="button" class="app-menu__item app-menu__item--action" @click="emit('logout')">
          Выйти
        </button>
      </UiMenu>
      <template v-if="!isAuthenticated">
        <UiButton variant="secondary" to="/login">Войти</UiButton>
        <UiButton to="/register">Регистрация</UiButton>
      </template>
    </div>
  </nav>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { NotificationCenter } from '@/modules/common/public'
import UiBadge from '@/shared/ui/UiBadge.vue'
import UiButton from '@/shared/ui/UiButton.vue'
import UiMenu from '@/shared/ui/UiMenu.vue'

defineProps<{
  isAuthenticated: boolean
  isAdmin: boolean
  isSupplier: boolean
  isBuyer: boolean
  isSupplierShowcaseEnabled: boolean
  isProcurementEnabled: boolean
  isChatEnabled: boolean
  userName: string
  cartItemsCount: number
  favoritesCount: number
}>()

const emit = defineEmits<{
  logout: []
}>()

const userMenuRef = ref<{ close: () => void } | null>(null)

function closeUserMenu() {
  userMenuRef.value?.close()
}
</script>

<style scoped>
.app-bar__nav {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  margin-left: auto;
}

.app-bar__links,
.app-bar__actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.app-bar__divider {
  width: 1px;
  height: 24px;
  background: rgba(255, 255, 255, 0.15);
  margin: 0 16px 0 4px;
  flex-shrink: 0;
}

.app-bar__nav-link {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 40px;
  padding: 0 12px;
  color: #fff;
  text-decoration: none;
  font-weight: 600;
  border-radius: 8px;
}

.app-bar__nav-link-icon {
  flex-shrink: 0;
}

.app-bar__nav-link:hover {
  background: #eff6ff;
  color: var(--color-accent);
}

.app-bar__nav-link.router-link-active {
  color: var(--color-accent);
  background: #eff6ff;
}

.app-bar__nav-link--catalog {
  background: #eff6ff;
  color: var(--color-accent);
  border: 1px solid #bfdbfe;
}

.app-bar__nav-link--catalog:hover {
  background: #dbeafe;
}

.app-bar__nav-link--catalog.router-link-active {
  background: var(--color-accent);
  color: #fff;
  border-color: var(--color-accent);
}

.app-bar__nav-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.app-bar__nav-btn--icon {
  width: 40px;
  min-width: 40px;
  padding: 0;
}

.app-bar__badge-wrap {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  font-size: 18px;
  line-height: 1;
  flex-shrink: 0;
}

.app-bar__badge-wrap :deep(.app-bar__badge) {
  position: absolute;
  top: -8px;
  right: -10px;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  font-size: 11px;
  line-height: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  box-sizing: border-box;
  pointer-events: none;
}

.app-bar__user-menu {
  margin-left: 4px;
}

.app-bar__user-trigger {
  height: 40px;
  padding: 0 10px;
  background: none;
  border: 1px solid rgba(255, 255, 255, 0.3);
  font: inherit;
  font-size: 14px;
  cursor: pointer;
  color: #fff;
  border-radius: 8px;
  white-space: nowrap;
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.app-bar__user-name {
  max-width: 140px;
  overflow: hidden;
  text-overflow: ellipsis;
}

@media (max-width: 1260px) {
  .app-bar__user-name {
    max-width: 90px;
  }
}

.app-bar__user-trigger:hover {
  background: rgba(255, 255, 255, 0.1);
}

.app-menu__item {
  display: block;
  width: 100%;
  padding: 10px 16px;
  text-align: left;
  text-decoration: none;
  color: #0f172a;
  font: inherit;
  background: none;
  border: none;
  cursor: pointer;
}

.app-menu__item:hover {
  background: #f1f5f9;
}

.app-menu__label {
  padding: 8px 16px 4px;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #586578;
}

.app-menu__divider {
  height: 1px;
  margin: 4px 0;
  background: #e2e8f0;
}

.app-menu__item--action {
  border-top: 1px solid #e2e8f0;
}

.app-bar__nav :deep(.ui-button) {
  height: 40px;
  padding: 0 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
}

.app-bar__nav :deep(.ui-button.app-bar__nav-btn--icon) {
  height: 40px;
  padding: 0;
}

@media (max-width: 1260px) {
  .app-bar__nav :deep(.ui-button) {
    padding: 0 10px;
    font-size: 14px;
  }

  .app-bar__nav-link {
    padding: 0 6px;
  }
}
</style>
